import { useEffect, useRef, useState, type FormEvent, type MouseEvent } from "react";
import { deleteEvent, updateEventParticipants } from "./api";
import type { Member, ScheduleEvent } from "./types";

interface Props {
  /** 同じ日にタイムラインへ表示中の予定。1件でも複数件でもよい。 */
  events: ScheduleEvent[];
  /** 最初に選んでおく予定。省略時は先頭。 */
  initialEventId?: string;
  members: Member[];
  onClose: () => void;
  onSaved: (eventId: string, memberIds: string[]) => void;
  onDeleted: (eventId: string) => void;
}

function eventTime(event: ScheduleEvent): string {
  if (event.allDay) {
    return event.start === event.end
      ? `${event.start}（終日）`
      : `${event.start}〜${event.end}（終日）`;
  }
  return `${event.start.replace("T", " ")}〜${event.end.replace("T", " ")}`;
}

function sameMembers(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const sortedB = [...b].sort();
  return [...a].sort().every((id, index) => id === sortedB[index]);
}

export default function EventEditorModal({
  events,
  initialEventId,
  members,
  onClose,
  onSaved,
  onDeleted,
}: Props) {
  const [selectedEventId, setSelectedEventId] = useState(
    events.some((event) => event.id === initialEventId) ? initialEventId : events[0]?.id,
  );
  // 選択中の予定が削除などで一覧から消えたら先頭へ戻す。
  const selectedEvent = events.find((event) => event.id === selectedEventId) ?? events[0];
  const [memberIds, setMemberIds] = useState<string[]>(selectedEvent?.memberIds ?? []);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const selectedEventButtonRef = useRef<HTMLButtonElement>(null);
  // 保存・削除の間はボタンが disabled になりフォーカスが body へ落ちるので、完了後に
  // 選択中の予定ボタンへ戻す（次の予定を選ぶ操作にそのまま続けられる）。
  const [restoreFocus, setRestoreFocus] = useState(false);
  const busy = saving || deleting;

  // 利用者がチェックを変えたか（未保存ガードに使う）。
  const changed = selectedEvent ? !sameMembers(memberIds, selectedEvent.memberIds) : false;
  // 画面に出ない担当者 ID（Notion 側で外れた人など）が残っていれば、変更が無くても保存して正規化できるようにする。
  const knownMemberIds = new Set(members.map((member) => member.id));
  const hasUnknownMembers = selectedEvent?.memberIds.some((id) => !knownMemberIds.has(id)) ?? false;
  const canSave = changed || hasUnknownMembers;

  /** 未保存の変更があれば確認してから閉じる。Escape・背景クリック・✕・閉じるボタンのすべてで使う。 */
  const requestClose = () => {
    if (busy) return;
    if (
      changed &&
      !window.confirm(
        `「${selectedEvent?.title ?? ""}」の担当者の変更を保存していません。\n閉じると変更は破棄されます。閉じますか？`,
      )
    ) {
      return;
    }
    onClose();
  };

  // 初回だけフォーカスを移し、背面のスクロールを止める。保存・削除で再実行しない。
  useEffect(() => {
    closeButtonRef.current?.focus();
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  // Escape は最新の requestClose を ref 経由で呼ぶ（依存配列で effect を作り直さない）。
  const requestCloseRef = useRef(requestClose);
  requestCloseRef.current = requestClose;
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") requestCloseRef.current();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // 選択中の予定が（削除で先頭へ戻るなど）差し替わったら、チェック状態もその予定に合わせる。
  const shownEventId = selectedEvent?.id;
  useEffect(() => {
    if (shownEventId && shownEventId !== selectedEventId) {
      setSelectedEventId(shownEventId);
      setMemberIds([...(events.find((event) => event.id === shownEventId)?.memberIds ?? [])]);
    }
  }, [events, selectedEventId, shownEventId]);

  useEffect(() => {
    if (restoreFocus && !busy) {
      selectedEventButtonRef.current?.focus();
      setRestoreFocus(false);
    }
  }, [busy, restoreFocus]);

  if (!selectedEvent) return null;

  const selectEvent = (eventId: string) => {
    const event = events.find((item) => item.id === eventId);
    if (!event || event.id === selectedEvent.id || busy) return;
    if (
      changed &&
      !window.confirm(
        `「${selectedEvent.title}」の担当者の変更を保存していません。\n切り替えると変更は破棄されます。切り替えますか？`,
      )
    ) {
      return;
    }
    setSelectedEventId(event.id);
    setMemberIds([...event.memberIds]);
    setError(null);
    setNotice(null);
  };

  const toggleMember = (memberId: string, checked: boolean) => {
    setMemberIds((current) =>
      checked
        ? [...new Set([...current, memberId])]
        : current.filter((id) => id !== memberId),
    );
  };

  const save = async (formEvent: FormEvent) => {
    formEvent.preventDefault();
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const result = await updateEventParticipants(selectedEvent.id, memberIds);
      onSaved(selectedEvent.id, result.memberIds);
      if (events.length <= 1) {
        onClose();
        return;
      }
      // 同じ日に他の予定がある場合は閉じず、続けて別の予定を編集できるようにする。
      setMemberIds([...result.memberIds]);
      setNotice(
        result.memberIds.length === 0
          ? `「${selectedEvent.title}」から全員の割り当てを解除しました。他の予定も続けて編集できます。`
          : `「${selectedEvent.title}」の担当者を${result.memberIds.length}名で保存しました。他の予定も続けて編集できます。`,
      );
      setRestoreFocus(true);
      setSaving(false);
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : "担当者を更新できませんでした");
      setSaving(false);
    }
  };

  const remove = async () => {
    const confirmed = window.confirm(
      `「${selectedEvent.title}」を削除しますか？\nNotionのゴミ箱へ移動します。`,
    );
    if (!confirmed) return;
    setDeleting(true);
    setError(null);
    setNotice(null);
    try {
      await deleteEvent(selectedEvent.id);
      onDeleted(selectedEvent.id);
      if (events.length <= 1) {
        onClose();
        return;
      }
      setNotice(`「${selectedEvent.title}」をNotionのゴミ箱へ移動しました。`);
      setRestoreFocus(true);
      setDeleting(false);
    } catch (deleteError) {
      setError(deleteError instanceof Error ? deleteError.message : "イベントを削除できませんでした");
      setDeleting(false);
    }
  };

  const closeFromBackdrop = (event: MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) requestClose();
  };

  return (
    <div className="modal-backdrop" onMouseDown={closeFromBackdrop}>
      <section
        className="event-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="event-editor-title"
      >
        <div className="modal-heading">
          <div>
            <h2 id="event-editor-title">イベントの担当者を編集</h2>
            <p>チェックを外すと、その担当者への割り当てを解除します。</p>
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            className="modal-close"
            aria-label="閉じる"
            disabled={busy}
            onClick={requestClose}
          >
            ✕
          </button>
        </div>

        <form onSubmit={(event) => void save(event)}>
          {events.length > 1 && (
            <div className="modal-event-list">
              <p id="modal-event-list-label">
                この日に表示中の予定が{events.length}件あります。編集する予定を選んでください。
              </p>
              <div role="group" aria-labelledby="modal-event-list-label">
                {events.map((event) => {
                  const selected = event.id === selectedEvent.id;
                  return (
                    <button
                      type="button"
                      key={event.id}
                      ref={selected ? selectedEventButtonRef : undefined}
                      className="modal-event-tab"
                      aria-pressed={selected}
                      disabled={busy}
                      onClick={() => selectEvent(event.id)}
                    >
                      <strong>{event.title}</strong>
                      <span>{eventTime(event)}</span>
                      <small>担当 {event.memberIds.length}名</small>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="modal-event-summary">
            <strong>{selectedEvent.title}</strong>
            <span>{eventTime(selectedEvent)}</span>
            <span>{selectedEvent.source === "outlook" ? "Outlookから登録" : "Notion予定"}</span>
          </div>

          {notice && <p className="modal-notice" role="status">{notice}</p>}

          <div className="modal-participants">
            {members.map((member) => (
              <label className="participant" key={member.id}>
                <input
                  type="checkbox"
                  checked={memberIds.includes(member.id)}
                  onChange={(event) => toggleMember(member.id, event.target.checked)}
                />
                {member.name}
              </label>
            ))}
          </div>

          {memberIds.length === 0 && (
            <p className="modal-warning">保存すると、このイベントから全員の割り当てが解除されます。</p>
          )}
          {error && <p className="modal-error" role="alert">{error}</p>}

          <div className="modal-actions">
            <button
              type="button"
              className="danger-action"
              disabled={busy}
              onClick={() => void remove()}
            >
              {deleting ? "削除中…" : "イベントを削除"}
            </button>
            <button type="button" disabled={busy} onClick={requestClose}>
              {events.length > 1 ? "閉じる" : "キャンセル"}
            </button>
            <button type="submit" className="primary-action" disabled={busy || !canSave}>
              {saving ? "保存中…" : memberIds.length === 0 ? "全員の割り当てを解除" : `担当者を保存（${memberIds.length}名）`}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}
